import { cn } from "@/lib/utils";
import { AnalyticsData } from "@/types/common.types";
import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { ShimmerLoader } from "../common/ShimmerLoader";
import {
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableHead,
  StyledTableHeader,
  StyledTableRow,
} from "../common/Table";
import Text from "../common/Text";

import { downloadFile } from "@/utils/downloadFile";
import { useDownloadAnalyticsStylesQuery } from "@/hooks/queries";
import { AnalyticsFilterState } from "@/types/analytics.types";
import useGetAnalyticsDataQuery from "@/hooks/queries/useGetAnalyticsDataQuery";

type AnalyticsDataTableProps = {
  selectedJobId: string | null;
  selectedFilters: AnalyticsFilterState;
  search: string;
  columnVisibility: VisibilityState;
  setColumnOptions: (options: Array<{ id: string; label: string }>) => void;
  setInitialSelectedColumns: (columns: string[]) => void;
};

const ANALYTICS_DATA_TABLE_COLUMNS_LABELS = {
  id: "ID",
  scheduledJobId: "Scheduled Job ID",
  jobStyleAssignmentId: "Job Style Assignment ID",
  styleId: "Style ID",
  dataSource: "Data Source",
  availableCount: "Available Count",
  mrp: "MRP",
  discountedPrice: "Discounted Price",
  gender: "Gender",
  brand: "Brand",
  articleType: "Article Type",
  processDate: "Process Date",
  createdBy: "Created By",
  createdDate: "Created Date",
  lastModifiedBy: "Last Modified By",
  lastModifiedAt: "Last Modified At",
};

const AnalyticsDataTable: React.FC<AnalyticsDataTableProps> = ({
  selectedJobId,
  selectedFilters,
  columnVisibility,
  setColumnOptions,
  setInitialSelectedColumns,
}) => {
  const [selectedStyleId, setSelectedStyleId] = useState<string>("");

  const [sorting, setSorting] = useState<SortingState>([]);
  const { data: analyticsData } = useGetAnalyticsDataQuery(
    selectedJobId as string,
    selectedFilters,
    {
      enabled: !!selectedJobId,
    }
  );

  const {
    refetch: downloadExcel,
    isSuccess,
    data: excelData,
  } = useDownloadAnalyticsStylesQuery(
    selectedJobId ? parseInt(selectedJobId) : null,
    selectedStyleId,
    {
      enabled: selectedStyleId.length > 0,
    }
  );

  useEffect(() => {
    if (isSuccess && excelData) {
      downloadFile(excelData, `analytics-data-${selectedJobId}.xlsx`);
    }
  }, [isSuccess, excelData, selectedJobId, selectedStyleId]);

  const handleStyleClick = async (styleId: string) => {
    if (!selectedJobId) return;
    setSelectedStyleId(styleId);
    const { data: excelData, isSuccess } = await downloadExcel();

    if (isSuccess && excelData) {
      downloadFile(
        excelData,
        `analytics-data-${selectedJobId}-${styleId}.xlsx`
      );
    }
  };

  const analyticsDataColumns: ColumnDef<AnalyticsData>[] = [
    {
      accessorKey: "id",
      header: "ID",
      cell: ({ row }) => <div>{row.original.id}</div>,
    },
    {
      accessorKey: "scheduledJobId",
      header: "Scheduled Job ID",
      cell: ({ row }) => <div>{row.original.scheduledJobId}</div>,
    },
    {
      accessorKey: "jobStyleAssignmentId",
      header: "Job Style Assignment ID",
      cell: ({ row }) => <div>{row.original.jobStyleAssignmentId}</div>,
    },
    {
      accessorKey: "styleId",
      header: "Style ID",
      cell: ({ row }) => (
        <div
          className="cursor-pointer capitalize text-company-purple hover:underline"
          onClick={() => handleStyleClick(row.original.styleId)}
        >
          {row.original.styleId}
        </div>
      ),
    },
    {
      accessorKey: "dataSource",
      header: "Data Source",
      cell: ({ row }) => <div>{row.original.dataSource}</div>,
    },
    {
      accessorKey: "availableCount",
      header: "Available Count",
      cell: ({ row }) => <div>{row.original.availableCount}</div>,
    },
    {
      accessorKey: "mrp",
      header: "MRP",
      cell: ({ row }) => <div>₹{row.original.mrp}</div>,
    },
    {
      accessorKey: "discountedPrice",
      header: "Discounted Price",
      cell: ({ row }) => <div>₹{row.original.discountedPrice}</div>,
    },
    {
      accessorKey: "gender",
      header: "Gender",
      cell: ({ row }) => <div>{row.original.gender}</div>,
    },
    {
      accessorKey: "brand",
      header: "Brand",
      cell: ({ row }) => <div>{row.original.brand}</div>,
    },
    {
      accessorKey: "articleType",
      header: "Article Type",
      cell: ({ row }) => <div>{row.original.articleType}</div>,
    },
    {
      accessorKey: "processDate",
      header: "Process Date",
      cell: ({ row }) => (
        <div>{new Date(row.original.processDate).toLocaleDateString()}</div>
      ),
    },
    {
      accessorKey: "createdBy",
      header: "Created By",
      cell: ({ row }) => <div>{row.original.createdBy || "-"}</div>,
    },
    {
      accessorKey: "createdDate",
      header: "Created Date",
      cell: ({ row }) => (
        <div>{new Date(row.original.createdDate).toLocaleString()}</div>
      ),
    },
    {
      accessorKey: "lastModifiedBy",
      header: "Last Modified By",
      cell: ({ row }) => <div>{row.original.lastModifiedBy || "-"}</div>,
    },
    {
      accessorKey: "lastModifiedAt",
      header: "Last Modified At",
      cell: ({ row }) => (
        <div>{new Date(row.original.lastModifiedAt).toLocaleString()}</div>
      ),
    },
  ];

  const table = useReactTable({
    data: selectedJobId ? (analyticsData as AnalyticsData[]) : [],
    columns: analyticsDataColumns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      columnVisibility,
    },
  });
  useEffect(() => {
    if (table) {
      const options = table
        .getAllColumns()
        .filter((column) => column.getCanHide())
        .map((column) => ({
          id: column.id,
          label:
            ANALYTICS_DATA_TABLE_COLUMNS_LABELS[
              column.id as keyof typeof ANALYTICS_DATA_TABLE_COLUMNS_LABELS
            ],
        }));
      setColumnOptions(options);

      const selected = table
        .getAllColumns()
        .filter((column) => column.getIsVisible())
        .map((column) => column.id);
      setInitialSelectedColumns(selected);
    }
  }, [table, setColumnOptions, setInitialSelectedColumns]);

  const renderCellContent = (cell: Cell<AnalyticsData, unknown>) => {
    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
    return content !== null && content !== undefined ? (
      content
    ) : (
      <ShimmerLoader height={16} width={16} />
    );
  };

  return (
    <AnalyticsDataTableHeader totalJobs={analyticsData?.length}>
      <StyledTable>
        <StyledTableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <StyledTableRow key={headerGroup.id}>
              {headerGroup.headers.map((header, index) => (
                <StyledTableHead
                  key={header.id}
                  className={cn("whitespace-nowrap", {
                    "px-4":
                      index === 0 || index === headerGroup.headers.length - 1,
                  })}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </StyledTableHead>
              ))}
            </StyledTableRow>
          ))}
        </StyledTableHeader>
        <StyledTableBody>
          {table.getRowModel().rows?.length ? (
            <>
              {table.getRowModel().rows.map((row) => (
                <StyledTableRow
                  key={row.id}
                 
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <StyledTableCell
                      key={cell.id}
                      className={cn("cursor-pointer", {
                        "px-4":
                          index === 0 ||
                          index === row.getVisibleCells().length - 1,
                      })}
                    >
                      {renderCellContent(cell)}
                    </StyledTableCell>
                  ))}
                </StyledTableRow>
              ))}
            </>
          ) : (
            <StyledTableRow>
              <StyledTableCell
                colSpan={table.getAllColumns().length}
                className="h-24 text-left"
              >
                No results.
              </StyledTableCell>
            </StyledTableRow>
          )}
        </StyledTableBody>
      </StyledTable>
    </AnalyticsDataTableHeader>
  );
};

export default AnalyticsDataTable;

type AnalyticsDataTableHeaderProps = {
  totalJobs?: number;
  children: React.ReactNode;
};

const AnalyticsDataTableHeader: React.FC<AnalyticsDataTableHeaderProps> = ({
  children,
  totalJobs,
}) => {
  return (
    <>
      {totalJobs && totalJobs > 0 ? (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing {totalJobs} Dataset
          </Text>
        </p>
      ) : (
        <p className="my-3">
          <Text className="text-[16px] font-normal leading-[28px] text-shades-neutral-300">
            Showing 0 Dataset
          </Text>
        </p>
      )}
      {children}
    </>
  );
};
